import React from 'react';
import { FaqMain } from '../../../../types/puppyGuide';

export const faq: FaqMain = {
  title: 'Vets Want You to Know',
  faqs: [
    {
      title: 'Puppies have different nutritional needs than adult dogs.',
      answer: (
        <React.Fragment>
          <p>
            Guidelines established by the AAFCO (Association of American Feed Control Officials)
            require almost every nutrient to be included in puppy food versus adult food. Because
            they are growing, puppies require more calories and higher levels of protein than adult
            dogs.
          </p>
          <p>
            Some companies have even developed formulas for different groups of puppies. For
            example, the amount of calories, protein, calcium, and phosphorus has to be perfect to
            establish a slow but steady growth and avoid any orthopedic problems for large-and-
            giant-breed puppies. You don’t want your Great Dane to grow too fast.
          </p>
          <p>
            There is no single best food for every puppy. By collaborating with your veterinarian,
            you can find a puppy food that you approve and that suits your puppy’s nutritional
            needs.
          </p>
        </React.Fragment>
      ),
    },
    {
      title: 'Dogs need different types of food at different life stages.',
      answer: (
        <React.Fragment>
          <p>
            As your puppy grows and matures into an adult dog, their nutritional needs will evolve
            as well. Puppies need food that helps them grow and provide them with energy. Dogs need
            food that supports and maintains their daily lifestyle.
          </p>
          <p>
            While the exact timing for switching to an adult dog food will be a decision between you
            and your veterinarian, most vets will recommend swapping to an adult dog food once your
            dog is close to one years old.
          </p>
          <p>
            Check out this article to learn more about puppy food and when it’s time to switch to
            adult food:
            <br />
            <a
              target="_blank"
              rel="noreferrer"
              href="https://be.chewy.com/the-ages-and-stages-of-dog-food-from-puppy-to-adult-to-senior/"
            >
              When to Switch to Adult Dog Food: A Guide to Feeding Dog Food at Different Life Stages
            </a>
            <br />
            <a
              target="_blank"
              rel="noreferrer"
              href="https://www.petmd.com/blogs/nutritionnuggets/jcoates/2012/may/special_nutritional_needs_of_puppies-23692"
            >
              What to Feed a Puppy and When to Switch to Adult Dog Food
            </a>
          </p>
        </React.Fragment>
      ),
    },
    {
      title: 'Establish set mealtimes with your puppy. ',
      answer: (
        <React.Fragment>
          <p>
            Establishing set mealtimes has a lot of benefits for puppy owners. Mealtimes ensure that
            you know how much to feed and when your puppy has eaten. You’ll also be able to notice
            if a change in appetite has started and address it immediately.
          </p>
          <p>
            Mealtimes also help to build consistent routines for your puppy, which is especially
            helpful during potty training. By having meals at certain times each day, you can
            anticipate your puppy’s potty needs and set them up for success.
          </p>
          <p>
            Most puppies do best with three meals a day until 6 months of age, then switching over
            to two meals a day. For toy- and small-breed dogs, four meals a day until 6 months, then
            three meals a day depending on the dog. Ask your vet about a specific feeding schedule
            to ensure you’re meeting your dog’s unique needs.
          </p>
          <p>
            Check out this guide to learn everything you need to know about feeding puppies:
            <br />
            <a target="_blank" rel="noreferrer" href="https://be.chewy.com/puppy-feeding-guide/">
              Puppy Feeding Guide: How Much to Feed a Puppy &amp; More
            </a>
          </p>
        </React.Fragment>
      ),
    },
    {
      title: 'Don’t forget that treats count towards your puppy’s daily calorie intake.',
      answer: (
        <React.Fragment>
          <p>
            To ensure you’re promoting proper dog nutrition, treats should be given in moderation
            and should represent 5% or less of your pet’s daily food intake. The rest should come
            from a nutritionally complete feeding program.
          </p>
          A few tips to follow when thinking about treats for your puppy:
          <ul>
            <li>
              Use treats strategically during training. The smaller the treat the better it is for
              training purposes, so that you don’t spoil mealtime.
            </li>
            <li>
              Think of treats as snacks, not food. Feeding your pet lots of treats can lead to
              excessive weight gain.
            </li>
            <li>Avoid feeding your puppy table scraps as treats.</li>
          </ul>
          <a
            target="_blank"
            rel="noreferrer"
            href="https://be.chewy.com/guide-many-treats-feed-dog/"
          >
            A Guide: How Many Treats Should I Feed My Dog
          </a>
          <br />
          <a
            target="_blank"
            rel="noreferrer"
            href="https://www.petmd.com/dog/nutrition/why-you-shouldnt-let-your-dog-eat-table-scraps"
          >
            Why You Shouldn’t Let Your Dog Eat Table Scraps
          </a>
          <br />
          <a
            target="_blank"
            rel="noreferrer"
            href="https://www.petmd.com/dog/training/tips-finding-your-pups-million-dollar-dog-training-treats"
          >
            Tips for Finding Your Pup’s Million-Dollar Dog Training Treats
          </a>
        </React.Fragment>
      ),
    },
    {
      title: 'Food bowls can collect dangerous bacteria if not cleaned regularly.',
      answer: (
        <React.Fragment>
          <p>
            Dried food material and saliva contain bacteria which can grow rapidly without cleaning.
          </p>
          <p>
            In fact, the National Sanitation Foundation ranked dog bowls as the fourth most germ-
            filled place in the home, after dish sponges and the kitchen sink. Bacteria like
            salmonella and E. coli are commonly found in dog bowls, as well as watering bowls, which
            can also contain algae and mold. These not only affect pet health, but can also make
            your human family members sick, too.
          </p>
          <p>
            Just wiping your pet’s bowls out with a damp cloth or rinsing them under warm water
            isn’t going to cut it. To eliminate bacteria, prevent dog illnesses and keep your entire
            family safe from these pathogens, pet dishes need to be cleaned properly.
          </p>
          Treat your puppy’s bowls like you would your own dishes:
          <ul>
            <li>Wash your puppy’s bowl with soap after every meal</li>
            <li>
              Empty and refresh your puppy’s water bowl daily and clean regularly to remove
              accumulating bacteria.
            </li>
          </ul>
          <p>
            Find out how to properly clean your puppy’s food bowl here:
            <br />
            <a
              target="_blank"
              rel="noreferrer"
              href="https://be.chewy.com/clean-dog-bowls-right-way/"
            >
              How to Clean Dog Bowls the Right Way
            </a>
          </p>
        </React.Fragment>
      ),
    },
  ],
};
